import React, { useState, useEffect } from "react";
import { collection, addDoc, query, where, getDocs, Timestamp, doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { useNavigate } from "react-router-dom";
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, TextField, DialogActions, Box, Grid, TableSortLabel } from "@mui/material";
import { Snackbar, Alert } from "@mui/material";

const StudentDashboard = () => {
  const [calls, setCalls] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    hrName: "",
    companyName: "",
    contactNumber: "",
    email: "",
    location: "",
    workMode: "",
    jobType: "",
    technology: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [sortConfig, setSortConfig] = useState({ key: "date", direction: "asc" });
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    if (!user || !user.uid) {
      navigate("/login");
      return;
    }

    const fetchCalls = async () => {
      if (!user || !user.uid) {
        navigate("/login");
        return;
      }
      try {
        const q = query(collection(db, "calls"), where("userId", "==", user.uid));
        const querySnapshot = await getDocs(q);
        const fetchedCalls = querySnapshot.docs.map(doc => {
          const data = doc.data();

          // Convert Firestore timestamp to Date
          let formattedDate = "N/A";
          if (data.createdAt && data.createdAt.seconds) {
            formattedDate = new Date(data.createdAt.seconds * 1000).toLocaleDateString(); // Convert to readable format
          }

          return {
            id: doc.id,
            ...data,
            date: formattedDate, // Store formatted date
          };
        });
        setCalls(fetchedCalls);
      } catch (error) {
        console.error("Error fetching calls:", error);
      }
    };

    fetchCalls();
  }, [navigate, user]);

  useEffect(() => {
    localStorage.setItem("hrCallsCount", calls.length);
  }, [calls])

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
    setFormData({
      hrName: "",
      companyName: "",
      contactNumber: "",
      email: "",
      location: "",
      workMode: "",
      jobType: "",
      technology: "",
    });
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && editingId) {
        const docRef = doc(db, "calls", editingId);
        await updateDoc(docRef, formData);

        setCalls(prev =>
          prev.map(item =>
            item.id === editingId ? { ...item, ...formData } : item
          )
        );
        setSnackbar({ open: true, message: "Updated successfully!", severity: "success" });
      } else {
        await addDoc(collection(db, "calls"), {
          ...formData,
          userId: user.uid,
          studentName: user.name,
          createdAt: Timestamp.now(),
        });

        setCalls([...calls, { ...formData, userId: user.uid, studentName: user.name }]);
        setSnackbar({ open: true, message: "Added Data successfully!", severity: "success" });
      }
      setFormData({ hrName: "", companyName: "", contactNumber: "", email: "", location: "", workMode: "", jobType: "", technology: "" });
      setEditMode(false);
      setEditingId(null);
      handleClose();

    } catch (error) {
      console.error("Error adding document: ", error);
      setSnackbar({ open: true, message: "Error Adding HR Data", severity: "error" });
    }
  };

  const sortedHrRecords = [...calls].sort((a, b) => {
    if (sortConfig.key === "date") {
      return sortConfig.direction === "asc"
        ? new Date(a.date) - new Date(b.date)
        : new Date(b.date) - new Date(a.date);
    }
    return sortConfig.direction === "asc"
      ? a[sortConfig.key]?.localeCompare(b[sortConfig.key])
      : b[sortConfig.key]?.localeCompare(a[sortConfig.key]);
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const editHrData = (call = null) => {
    if (call) {
      setFormData({
        hrName: call.hrName || "",
        companyName: call.companyName || "",
        contactNumber: call.contactNumber || "",
        email: call.email || "",
        location: call.location || "",
        workMode: call.workMode || "",
        jobType: call.jobType || "",
        technology: call.technology || "",
      });
      setEditingId(call.id);
      setEditMode(true);
    } else {
      setFormData({
        hrName: "",
        companyName: "",
        contactNumber: "",
        email: "",
        location: "",
        workMode: "",
        jobType: "",
        technology: "",
      });
      setEditMode(false);
      setEditingId(null);
    }

    setOpen(true);
  };

  return (
    <div style={{ padding: "20px" }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>



      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <h3 style={{ color: '#9c27b0' }}>HR Data Records</h3>
        <Button variant="contained" color="primary" onClick={handleOpen} >
          Add HR Data
        </Button>
      </Box>



      {/* Table to Show Calls */}
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
              <TableCell><strong>HR Name</strong></TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === "technology"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("technology")}
                >
                  <strong>Technology</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === "companyName"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("companyName")}
                >
                  <strong>Company Name</strong>
                </TableSortLabel></TableCell>
              <TableCell><strong>Location</strong></TableCell>
              <TableCell><strong>Contact Number</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Work Mode</strong></TableCell>
              <TableCell><strong>Job Type</strong></TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === "date"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("date")}
                >
                  <strong>Date</strong>
                </TableSortLabel></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {calls.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: "gray", fontStyle: "italic" }}>
                  No records available
                </TableCell>
              </TableRow>
            ) : (
              sortedHrRecords.map((call) => (
                <TableRow key={call.id} onClick={() => editHrData(call)} hover style={{ cursor: "pointer" }}>
                  <TableCell>{call.hrName}</TableCell>
                  <TableCell>{call.technology}</TableCell>
                  <TableCell>{call.companyName}</TableCell>
                  <TableCell>{call.location}</TableCell>
                  <TableCell>{call.contactNumber}</TableCell>
                  <TableCell>{call.email}</TableCell>
                  <TableCell>{call.workMode}</TableCell>
                  <TableCell>{call.jobType}</TableCell>
                  <TableCell>{call.date}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>


      {/* New Call Form - Updated UI */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle color="secondary">{editMode ? "Edit HR Data" : "Add HR Data"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} marginTop={1}>
            <Grid item xs={6}>
              <TextField label="HR Name" name="hrName" fullWidth onChange={handleChange} value={formData.hrName} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Technology" name="technology" fullWidth onChange={handleChange} value={formData.technology} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Company Name" name="companyName" fullWidth onChange={handleChange} value={formData.companyName} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Location" name="location" fullWidth onChange={handleChange} value={formData.location} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Contact Number" name="contactNumber" fullWidth onChange={handleChange} value={formData.contactNumber} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Email" name="email" fullWidth onChange={handleChange} value={formData.email} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Work Mode" name="workMode" fullWidth onChange={handleChange} value={formData.workMode} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Job Type" name="jobType" fullWidth onChange={handleChange} value={formData.jobType} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">Cancel</Button>
          <Button onClick={handleSubmit} color="primary">{editMode ? "Update" : "Submit"}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StudentDashboard;
